import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Mail, MessageCircle, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [status, setStatus] = useState(null); // null | 'loading' | 'ok' | 'err'
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("err");
      setError("Compila tutti i campi obbligatori.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await api.post("/contact", form);
      setStatus("ok");
      setForm({ name: "", email: "", subject: "general", message: "" });
    } catch (e) {
      setStatus("err");
      setError(e?.response?.data?.detail || "Errore invio. Riprova più tardi.");
    }
  };

  return (
    <div className="min-h-screen" data-testid="page-contatti">
      <Seo
        title="Contatti"
        description="Mettiti in contatto con BrickDailyDeals: segnalazioni offerte, collaborazioni, supporto. Email, modulo contatti e canale Telegram."
        path="/contatti"
      />
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          data-testid="contatti-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8"
        >
          <ArrowLeft size={14} /> Home
        </Link>

        <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-3">
          Contatti
        </div>
        <h1 className="font-display font-black text-4xl text-white mb-3 tracking-tight">
          Mettiamoci in contatto
        </h1>
        <p className="text-zinc-400 text-lg mb-10">
          Siamo qui per ascoltarti. Scegli il canale che preferisci o usa il
          modulo qui sotto: ti risponderemo entro 48 ore.
        </p>

        {/* Quick channels */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <a
            href="https://t.me/BrickDailyDeals"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contatti-telegram"
            className="group bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#229ED9]/50 rounded-2xl p-5 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#229ED9]/15 flex items-center justify-center shrink-0">
                <Send size={20} className="text-[#229ED9]" />
              </div>
              <div>
                <div className="font-bold text-white mb-1">Canale Telegram</div>
                <div className="text-sm text-zinc-400">
                  Risposta veloce dalla community. Scrivi a @BrickDailyDeals.
                </div>
              </div>
            </div>
          </a>
          <a
            href="mailto:info@dailybrickdeals.com"
            data-testid="contatti-email"
            className="group bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#FBD51C]/50 rounded-2xl p-5 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#FBD51C]/15 flex items-center justify-center shrink-0">
                <Mail size={20} className="text-[#FBD51C]" />
              </div>
              <div>
                <div className="font-bold text-white mb-1">Email diretta</div>
                <div className="text-sm text-zinc-400">
                  info@dailybrickdeals.com
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <MessageCircle size={18} className="text-[#FBD51C]" />
            <h2 className="font-display text-xl text-white">Invia un messaggio</h2>
          </div>

          {status === "ok" ? (
            <div
              data-testid="contatti-success"
              className="flex items-start gap-3 p-4 rounded-xl border border-emerald-900/50 bg-emerald-950/40 text-emerald-300"
            >
              <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-bold mb-1">Messaggio inviato!</div>
                <div className="text-sm text-emerald-300/80">
                  Ti ringraziamo. Riceverai una risposta entro 48 ore lavorative.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contatti-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
                    Nome <span className="text-rose-400">*</span>
                  </label>
                  <input
                    data-testid="contatti-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
                    Email <span className="text-rose-400">*</span>
                  </label>
                  <input
                    data-testid="contatti-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
                  Argomento
                </label>
                <select
                  data-testid="contatti-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                >
                  <option value="general">Informazione generale</option>
                  <option value="deal">Segnalazione offerta</option>
                  <option value="partnership">Proposta di collaborazione</option>
                  <option value="bug">Segnalazione errore / bug</option>
                  <option value="advertising">Pubblicità / inserzioni</option>
                  <option value="privacy">Richiesta privacy / GDPR</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
                  Messaggio <span className="text-rose-400">*</span>
                </label>
                <textarea
                  data-testid="contatti-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60 resize-y"
                />
              </div>

              {status === "err" && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-900/50 bg-rose-950/40 text-rose-300 text-sm">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <p className="text-xs text-zinc-500">
                Inviando il modulo accetti il trattamento dei dati secondo la
                nostra <Link to="/privacy" className="text-[#FBD51C] hover:underline">Privacy Policy</Link>.
                I tuoi dati saranno usati solo per rispondere a questa richiesta.
              </p>

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contatti-submit"
                className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] disabled:opacity-60 text-black font-black uppercase tracking-wider text-xs px-6 py-3 rounded-full brand-cta-glow"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Invio...
                  </>
                ) : (
                  <>
                    <Send size={16} /> Invia messaggio
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        <div className="mt-10 text-xs text-zinc-500 leading-relaxed">
          BrickDailyDeals · Progetto editoriale indipendente · I dati inviati
          tramite questo modulo vengono trattati nel rispetto della normativa
          GDPR (UE 2016/679) e conservati solo per il tempo necessario a
          rispondere alla richiesta.
        </div>
      </div>
      <Footer />
    </div>
  );
}
